<template>
  <div class="container-fluid p-3">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="h3 my-0 mx-0 text-foreground">Instagram Widget</h3>
        <p class="text-muted-foreground text-sm">Manage your Instagram widgets and installations</p>
      </div>
      <div class="flex items-center space-x-4">
        <button
          class="btn round-xl px-4 create-customer-btn"
          @click="showCreateCustomerModal = true"
        >
          Create Customer
        </button>
        <button
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          @click="showCreateWidgetModal = true"
        >
          <i class="uil uil-plus"></i>
          <span class="text-sm font-medium"> Create Widget</span>
        </button>
      </div>
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />

    <!-- Main Content -->
    <div class="main-content-wrapper">
        <!-- Controls -->
        <div class="controls-bar">
          <div class="controls-left">
            <span class="show-label">Show</span>
            <b-form-select
              v-model="datatable.queries.per_page"
              class="show-select"
              :options="[10, 15, 25, 50, 100]"
              @change="getWidgets"
            ></b-form-select>
          </div>
          <div class="controls-right">
            <span class="search-label">Search:</span>
            <input
              v-model="datatable.queries.search"
              type="search"
              class="search-input"
              placeholder="Search..."
              @keyup.enter="getWidgets"
            >
          </div>
        </div>

        <!-- Table -->
        <div class="table-container rounded-md border border-border overflow-hidden mt-4">
          <div class="table-wrapper">
            <template v-if="!loadingWidgets">
              <table class="data-table">
                <thead>
                  <tr class="table-header-row">
                    <th class="table-header">#</th>
                    <th class="table-header">Widget</th>
                    <th class="table-header">Instagram Page</th>
                    <th class="table-header">Installation</th>
                    <th class="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="widget in widgets"
                    :key="widget.id"
                    class="table-row"
                  >
                    <td class="table-cell font-medium">{{ widget.id }}</td>
                    <td class="table-cell">{{ widget.widget_title }}</td>
                    <td class="table-cell">
                      <button
                        class="view-page-btn"
                        :disabled="gettingPage"
                        @click="viewPage(widget)"
                      >
                        <b-spinner v-if="gettingPage" small class="mr-1"></b-spinner>
                        View Page
                      </button>
                    </td>
                    <td class="table-cell">
                      <button
                        class="add-widget-btn"
                        @click="addWidget(widget.id)"
                      >
                        Add Widget
                      </button>
                    </td>
                    <td class="table-cell text-right">
                      <div class="actions-group">
                        <button
                          class="edit-widget-btn"
                          :disabled="gettingWidget"
                          @click="editWidget(widget)"
                        >
                          <b-spinner v-if="gettingWidget" small class="mr-1"></b-spinner>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="edit-icon">
                            <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                          </svg>
                          Edit Widget
                        </button>
                        <button
                          class="delete-btn"
                          :disabled="loadingRemove === widget.id"
                          @click="remove(widget)"
                        >
                          <b-spinner v-if="loadingRemove === widget.id" small class="mr-1"></b-spinner>
                          <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="delete-icon">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            <line x1="10" x2="10" y1="11" y2="17"></line>
                            <line x1="14" x2="14" y1="11" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <div v-else class="loading-state">
              <b-spinner></b-spinner>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
          <span>Total {{ totalWidgets }} Widgets</span>
          <div class="flex items-center space-x-2">
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
              :disabled="datatable.queries.page === 1"
              @click="datatable.queries.page > 1 && (datatable.queries.page--, onPageChanged(datatable.queries.page))"
            >
              «
            </button>
            <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80 request-review-btn" style="background-color: rgb(36, 99, 235); color: #ffffff;">
              {{ datatable.queries.page }}
            </div>
            <button
              class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
              :disabled="datatable.queries.page >= Math.ceil(totalWidgets / datatable.queries.per_page)"
              @click="datatable.queries.page < Math.ceil(totalWidgets / datatable.queries.per_page) && (datatable.queries.page++, onPageChanged(datatable.queries.page))"
            >
              »
            </button>
          </div>
        </div>
      </div>

    <!-- Delete Modal -->
    <b-modal v-model="showDeleteModal" title="Delete Widget">
      <p v-if="selectedBusiness">
        Are you sure you want to delete widget "{{ selectedBusiness.widget_title }}"?
      </p>
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteModal = false">Cancel</b-button>
        <b-button variant="danger" @click="handleRemove">Delete</b-button>
      </template>
    </b-modal>

    <!-- Create Widget Modal -->
    <b-modal v-model="showCreateWidgetModal" title="Create Widget" :hide-footer="true">
      <span>Widget Title</span>
      <input
        v-model="newWidgetTitle"
        type="text"
        class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-2"
        placeholder="Enter widget title"
      >
      <button
        class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 py-2 bg-foreground hover:bg-foreground/90 text-background font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-6 mt-3"
        @click="createWidget"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-plus mr-2 h-4 w-4">
          <path d="M5 12h14"></path>
          <path d="M12 5v14"></path>
        </svg>
        Create Widget
      </button>
    </b-modal>

    <!-- Add Widget Modal -->
    <b-modal v-model="showAddWidgetModal" title="Add Widget" size="lg" :hide-footer="true">
      <textarea
        :value="embedHTML"
        cols="30"
        rows="5"
        class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
        style="white-space: pre-line"
        readonly
      ></textarea>
      <b-button class="mt-5" @click="copyToClipboard">Copy Widget</b-button>
    </b-modal>

    <!-- Create Customer Modal (placeholder - functionality may need to be implemented) -->
    <b-modal v-model="showCreateCustomerModal" title="Create Customer" :hide-footer="true">
      <p class="text-muted-foreground">Customer creation functionality to be implemented.</p>
    </b-modal>

    <!-- Edit Widget Modal (new UI/UX - stays in new business layout) -->
    <b-modal
      v-model="showEditWidgetModal"
      :hide-footer="true"
      body-class="edit-widget-modal-body-wrapper"
      header-class="align-items-center"
      modal-class="edit_widget_modal widget-editor widget-modal"
      size="lg"
    >
      <template v-slot:modal-header>
        <h5 class="text-white">Edit Widget</h5>
        <div class="float-right">
          <button class="btn btn-default btn-sm mr-2" @click="showEditWidgetModal = false">Cancel</button>
          <button class="btn btn-success btn-sm mr-2" @click="saveEditor(false)">
            <b-spinner v-if="savingEditor" style="width: 1rem; height: 1rem;"></b-spinner>
            Save
          </button>
          <button class="btn btn-success btn-sm" @click="saveEditor(true)">
            <b-spinner v-if="savingEditor" style="width: 1rem; height: 1rem;"></b-spinner>
            Save & Exit
          </button>
        </div>
      </template>
      <div class="edit-widget-modal-body insta-widget-editor-layout" style="display: flex; flex-direction: row; flex: 1; min-height: 0; width: 100%;">
        <div class="container-fluid" style="display: flex; flex: 1; min-width: 0; padding: 0;">
          <div class="row" style="display: flex; flex-direction: row; flex-wrap: nowrap; flex: 1; width: 100%; margin: 0;">
            <div id="Navbar" class="sidebar insta-widget-editor-sidebar" style="flex: 0 0 360px; width: 360px; max-width: 360px; display: flex;">
              <div class="left-nav-side float-left" style="display: flex; flex-direction: column; width: 56px; min-width: 56px;">
                <button type="button" :class="{ active: editor.editing === 'source' }" @click="editor.editing = 'source'">
                  <i class="uil uil-search d-block"></i>
                  Source
                </button>
                <button type="button" :class="{ active: editor.editing === 'layout' }" @click="editor.editing = 'layout'">
                  <i class="uil uil-window-grid d-block"></i>
                  Layout
                </button>
                <button type="button" :class="{ active: editor.editing === 'post_style' }" @click="editor.editing = 'post_style'">
                  <i class="uil uil-brush-alt d-block"></i>
                  Post Style
                </button>
                <button type="button" :class="{ active: editor.editing === 'style' }" @click="editor.editing = 'style'">
                  <i class="uil uil-image-edit d-block"></i>
                  Style
                </button>
              </div>
              <ul class="nav nav-sidebar">
                <li v-if="editor.editing === 'source'" class="source">
                  <h4>Source</h4>
                  <ul class="list-unstyled">
                    <li class="d-flex">
                      <input v-model="editor.source.username" type="text" class="source_input">
                      <button class="source_button" @click="getWidgetForEdit(true)">
                        <i class="uil uil-search"></i>
                      </button>
                    </li>
                    <li class="widget_theme">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Search Type</span>
                        <v-select
                          v-model="editor.source.type"
                          :clearable="false"
                          :options="[
                            { label: 'Username', value: 'username' },
                            { label: 'Hashtag', value: 'hashtag' },
                          ]"
                          :reduce="option => option.value"
                        />
                      </span>
                    </li>
                  </ul>
                </li>
                <li v-if="editor.editing === 'layout'" class="layout">
                  <h4>Layout</h4>
                  <ul class="list-unstyled">
                    <li class="layouts_wrapper">
                      <span v-for="layout in layouts" :key="layout.name" class="layout-item">
                        <input
                          :id="'layout-' + layout.name"
                          v-model="editor.layout.layoutStyle"
                          type="radio"
                          class="btn-check"
                          name="layout-style-radio"
                          autocomplete="off"
                          :value="layout.name"
                        >
                        <label :for="'layout-' + layout.name" class="btn layout-option-btn">
                          <img :alt="layout.label" :src="'/images/widget-layouts/' + layout.name + '-icon.png'">
                          <span>{{ layout.label }}</span>
                        </label>
                      </span>
                    </li>
                    <li>
                      <span>Number of Columns</span>
                      <v-select v-model="editor.layout.columns" :clearable="false" taggable :options="[2, 3, 4]" />
                    </li>
                    <li>
                      <span>Number of Rows</span>
                      <v-select v-model="editor.layout.rows" :clearable="false" taggable :options="[1, 2, 3, 4]" />
                    </li>
                    <li>
                      <span>Gap Between Posts</span>
                      <v-select v-model="editor.layout.padding" :clearable="false" taggable :options="[0, 5, 10, 20, 30]" />
                    </li>
                    <li>
                      <span>Profile Details</span>
                      <ToggleButton v-model="editor.layout.show_profile_details" :height="20" :width="45" color="#00b050" :sync="true" />
                    </li>
                    <li v-if="editor.layout.show_profile_details">
                      <ul class="list-unstyled profile_details">
                        <li>
                          <span>Profile Picture</span>
                          <ToggleButton v-model="editor.layout.profile_details.profile_image" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Username</span>
                          <ToggleButton v-model="editor.layout.profile_details.username" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Follow Button</span>
                          <ToggleButton v-model="editor.layout.profile_details.follow_button" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Followers</span>
                          <ToggleButton v-model="editor.layout.profile_details.followers" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Following</span>
                          <ToggleButton v-model="editor.layout.profile_details.following" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Posts</span>
                          <ToggleButton v-model="editor.layout.profile_details.post_count" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Name</span>
                          <ToggleButton v-model="editor.layout.profile_details.name" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li v-if="editor.editing === 'post_style'" class="layout">
                  <h4>Post Style</h4>
                  <ul class="list-unstyled">
                    <li class="post_style_wrapper">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Post Style</span>
                        <v-select
                          v-model="editor.post_style.post_style"
                          :clearable="false"
                          :options="[
                            { label: 'Expanded', value: 'expanded' },
                            { label: 'Image Card', value: 'image_card' },
                          ]"
                          :reduce="option => option.value"
                        />
                      </span>
                    </li>
                    <li v-if="editor.post_style.post_style === 'expanded'" class="post_details">
                      <ul class="list-unstyled profile_details">
                        <li>
                          <span>Profile Picture</span>
                          <ToggleButton v-model="editor.post_style.post_details.profile_image" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Username</span>
                          <ToggleButton v-model="editor.post_style.post_details.username" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Likes Count</span>
                          <ToggleButton v-model="editor.post_style.post_details.likes_count" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Comments Count</span>
                          <ToggleButton v-model="editor.post_style.post_details.comments_count" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                        <li>
                          <span>Caption</span>
                          <ToggleButton v-model="editor.post_style.post_details.caption" :height="20" :width="45" color="#00b050" :sync="true" />
                        </li>
                      </ul>
                    </li>
                    <li class="post_action_wrapper">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Post Action</span>
                        <v-select
                          v-model="editor.post_style.post_action"
                          :clearable="false"
                          :searchable="false"
                          append-to-body
                          :options="[
                            { label: 'Open in Modal', value: 'modal' },
                            { label: 'Open on Instagram', value: 'instagram' },
                          ]"
                          :reduce="option => option.value"
                        />
                      </span>
                    </li>
                  </ul>
                </li>
                <li v-if="editor.editing === 'style'" class="layout">
                  <h4>Widget Style</h4>
                  <ul class="list-unstyled">
                    <li class="widget_theme">
                      <span class="layout-item d-flex justify-content-between">
                        <span>Widget Theme</span>
                        <v-select
                          v-model="editor.styles.theme"
                          :clearable="false"
                          :options="[
                            { label: 'Light', value: 'light' },
                            { label: 'Dark', value: 'dark' },
                          ]"
                          :reduce="option => option.value"
                        />
                      </span>
                    </li>
                    <li class="header_styles">
                      <span>Header Background</span>
                      <PopperColor v-model="editor.styles.header_bg" title="Header Background" />
                    </li>
                    <li class="header_styles">
                      <span>Header Text Color</span>
                      <PopperColor v-model="editor.styles.header_text" title="Header Text Color" />
                    </li>
                    <li class="header_styles">
                      <span>Follow Button Background</span>
                      <PopperColor v-model="editor.styles.follow_button_bg" title="Follow Button Background" />
                    </li>
                    <li class="header_styles">
                      <span>Follow Button Text Color</span>
                      <PopperColor v-model="editor.styles.follow_button_text" title="Follow Button Text Color" />
                    </li>
                    <li class="header_styles">
                      <span>Load More Button Background</span>
                      <PopperColor v-model="editor.styles.load_more_button_bg" title="Load More Button Background" />
                    </li>
                    <li class="header_styles">
                      <span>Load More Button Text Color</span>
                      <PopperColor v-model="editor.styles.load_more_button_text" title="Load More Button Text Color" />
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
            <div class="editor-content instagram-widget" :class="{ 'dark': editor.styles && editor.styles.theme === 'dark' }" style="flex: 1; min-width: 0; overflow-y: auto; overflow-x: hidden;">
              <div v-if="profileFetchState === ''" class="fetch_loader instagram-stream-idle-message">
                <p class="text-muted-foreground mb-0">Enter an Instagram username in the <strong>Source</strong> section and click the search button to preview.</p>
                <p class="text-muted-foreground small mt-2 mb-0">You don't need to connect to social media.</p>
              </div>
              <div v-else-if="profileFetchState === 'fetching' || profileFetchState === 'error'" class="fetch_loader">
                <b-spinner v-if="profileFetchState === 'fetching'"></b-spinner>
                <h3 v-if="profileFetchState === 'error'" class="error_fetching">{{ profileFetchMessage }}</h3>
              </div>
              <div v-else-if="profileFetchState === 'success'">
                <div v-if="postsType === 'profile' && editor.layout && editor.layout.show_profile_details">
                  <div :style="{ backgroundColor: editor.styles.header_bg, color: editor.styles.header_text }" class="profile_details_container">
                    <div class="profile_details_left">
                      <div v-if="editor.layout.profile_details.profile_image && profile && profile.profile_picture_url" class="profile_picture_container rounded rounded-circle">
                        <img :src="profile.profile_picture_url || '/images/widget-layouts/profile.jpg'" class="profile_picture">
                      </div>
                      <div class="profile_names_container">
                        <div class="username_container">
                          <h3 v-if="editor.layout.profile_details.name" class="name" :style="{ color: editor.styles.header_text }">{{ profile.name }}</h3>
                        </div>
                        <div class="username_container">
                          <h3 v-if="editor.layout.profile_details.username" class="username" :style="{ color: editor.styles.header_text }">{{ profile.username }}</h3>
                        </div>
                      </div>
                    </div>
                    <div class="profile_insights_container">
                      <p v-if="editor.layout.profile_details.post_count" class="insight_count_tag" :style="{ color: editor.styles.header_text }">
                        <strong class="post_count" :style="{ color: editor.styles.header_text }">{{ formatNumber(profile.media_count) }}</strong> posts
                      </p>
                      <p v-if="editor.layout.profile_details.followers" class="insight_count_tag" :style="{ color: editor.styles.header_text }">
                        <strong class="followers_count" :style="{ color: editor.styles.header_text }">{{ formatNumber(profile.followers_count) }}</strong> followers
                      </p>
                      <p v-if="editor.layout.profile_details.following" class="insight_count_tag" :style="{ color: editor.styles.header_text }">
                        <strong class="following_count" :style="{ color: editor.styles.header_text }">{{ formatNumber(profile.follows_count) }}</strong> following
                      </p>
                    </div>
                    <div class="follow_button_container">
                      <button v-if="editor.layout.profile_details.follow_button" class="follow_button" :style="{ backgroundColor: editor.styles.follow_button_bg, color: editor.styles.follow_button_text }" @click="redirectToProfile">
                        <i class="uil uil-instagram"></i> Follow
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="editor.layout && editor.layout.layoutStyle === 'grid'" class="posts_container grid_layout_posts_container" :style="{ gridTemplateColumns: 'repeat(' + (editor.layout.columns || 3) + ', 1fr)', gap: (editor.layout.padding || 5) + 'px' }">
                  <div v-for="post in imageCardPosts" :key="post.id" class="post image_card" @click="post_action(post)">
                    <div class="media_wrapper">
                      <i class="media_icon" :class="{ 'uil uil-play': post.media_type === 'VIDEO', 'uil uil-layers': post.media_type === 'CAROUSEL_ALBUM' }"></i>
                      <img v-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image" alt="Post">
                      <img v-else-if="post.media_type === 'CAROUSEL_ALBUM'" :src="post.media_url" class="post_image" alt="Post">
                      <video v-if="post.media_type === 'VIDEO'" :src="post.media_url" class="post_image"></video>
                    </div>
                    <div class="post_insights_container">
                      <span class="d-flex gap-5">
                        <p class="post_like"><i class="uil uil-heart-alt"></i>{{ formatNumber(post.like_count) }}</p>
                        <p class="post_like ml-3"><i class="uil uil-comment"></i>{{ formatNumber(post.comments_count) }}</p>
                      </span>
                    </div>
                  </div>
                  <div v-for="post in expandedPosts" :key="'exp-' + post.id" class="post expanded">
                    <div class="post_header justify-content-between align-items-center">
                      <div class="d-flex justify-content-start align-items-center">
                        <div v-if="editor.post_style.post_details.profile_image && profile && profile.profile_picture_url" class="profile_image_container">
                          <img :src="profile.profile_picture_url || '/images/widget-layouts/profile.jpg'" :alt="profile.name" class="profile_image">
                        </div>
                        <div v-if="editor.post_style.post_details.username" class="post_username_container mx-1">
                          <h3 class="username">{{ profile.username }}</h3>
                        </div>
                      </div>
                      <div class="font-size-24 mx-2">
                        <a :href="'https://instagram.com/' + profile.username" target="_blank" rel="noopener"><i class="uil uil-instagram instagram_icon"></i></a>
                      </div>
                    </div>
                    <div class="post_image_container" @click="post_action(post)">
                      <div class="media_wrapper">
                        <img v-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image" alt="Post">
                        <video v-if="post.media_type === 'VIDEO'" :src="post.media_url" class="post_image"></video>
                      </div>
                    </div>
                    <div class="post_footer" @click="post_action(post)">
                      <div class="post_insights_container">
                        <span class="d-flex gap-5">
                          <p v-if="editor.post_style.post_details.likes_count" class="post_like"><i class="uil uil-heart-alt"></i>{{ formatNumber(post.like_count) }}</p>
                          <p v-if="editor.post_style.post_details.comments_count" class="post_like ml-2"><i class="uil uil-comment"></i>{{ formatNumber(post.comments_count) }}</p>
                        </span>
                      </div>
                      <div v-if="editor.post_style.post_details.caption" class="post_caption_container">
                        <p class="caption">{{ post.caption }}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="showLoadMore && editor.layout && editor.layout.layoutStyle === 'grid'" class="load_more_container">
                  <button class="load_more_button" :style="{ backgroundColor: editor.styles.load_more_button_bg, color: editor.styles.load_more_button_text }" @click="loadMore">Load More</button>
                </div>
                <div v-if="editor.layout && editor.layout.layoutStyle === 'slider'" class="posts_container slider_layout_posts_container">
                  <Swiper :slides-per-view="editor.layout.columns" :space-between="editor.layout.padding" :modules="modules" navigation :loop="true" class="mySwiper">
                    <SwiperSlide v-for="post in posts" :key="post.id">
                      <div v-if="editor.post_style.post_style === 'image_card' && post.media_url" class="post image_card" @click="post_action(post)">
                        <div class="media_wrapper">
                          <img v-if="post.media_type === 'IMAGE'" :src="post.media_url" class="post_image" alt="Post">
                          <video v-else-if="post.media_type === 'VIDEO'" :src="post.media_url" class="post_image"></video>
                        </div>
                        <div class="post_insights_container">
                          <p class="post_like"><i class="uil uil-heart-alt"></i> {{ formatNumber(post.like_count) }}</p>
                        </div>
                      </div>
                    </SwiperSlide>
                  </Swiper>
                </div>

                <b-modal v-model="showPostModal" :hide-footer="true" title="Post" size="lg">
                  <div v-if="modalPost && modalPost.media_url" class="modal_posts_container">
                    <div class="post expanded">
                      <div class="post_image_container">
                        <img v-if="modalPost.media_type === 'IMAGE'" :src="modalPost.media_url" class="post_image" alt="Post">
                        <video v-else-if="modalPost.media_type === 'VIDEO'" :src="modalPost.media_url" class="post_image" controls></video>
                      </div>
                      <div class="post_footer">
                        <div class="post_insights_container">
                          <p class="post_like"><strong>{{ formatNumber(modalPost.like_count) }} Likes</strong></p>
                        </div>
                        <div v-if="modalPost.caption" class="post_caption_container">
                          <p class="caption"><strong>{{ profile.username }}</strong> {{ modalPost.caption }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </b-modal>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/swiper-vue.mjs'
import 'swiper/swiper-bundle.css'
import { ToggleButton } from 'vue-js-toggle-button'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import PopperColor from '@components/Common/PopperColor'
import '../../../design/custom/components/_dashboard.scss'

export default {
  name: 'InstaWidget',
  components: {
    Swiper,
    SwiperSlide,
    ToggleButton,
    'v-select': vSelect,
    PopperColor,
  },
  setup() {
    return {
      modules: [Navigation, Pagination, Scrollbar, A11y],
    }
  },
  data() {
    return {
      loadingWidgets: false,
      loadingRemove: null,
      showDeleteModal: false,
      showCreateWidgetModal: false,
      showAddWidgetModal: false,
      showCreateCustomerModal: false,
      showEditWidgetModal: false,
      showPostModal: false,
      selectedBusiness: null,
      selectedWidget: null,
      newWidgetTitle: '',
      gettingWidget: false,
      gettingPage: false,
      widgets: [],
      widgetId: null,
      profileFetchState: '',
      profileFetchMessage: '',
      profile: { username: '', name: '', profile_picture_url: '', followers_count: '', follows_count: '', media_count: '' },
      posts: [{ id: '', like_count: '', comments_count: '', caption: '', media_url: '' }],
      postsType: 'profile',
      additionalPostsToShow: 0,
      modalPost: { id: '', like_count: '', comments_count: '', caption: '', media_url: '' },
      savingEditor: false,
      layouts: [
        { label: 'Grid', name: 'grid' },
        { label: 'Slider', name: 'slider' },
      ],
      editor: {
        source: { username: '', type: 'username' },
        layout: {
          layoutStyle: 'grid',
          rows: 3,
          columns: 3,
          show_profile_details: true,
          profile_details: {
            profile_image: true,
            username: true,
            name: true,
            follow_button: true,
            post_count: true,
            followers: true,
            following: true,
          },
          padding: 5,
        },
        post_style: {
          post_style: 'image_card',
          post_details: {
            profile_image: true,
            username: true,
            likes_count: true,
            comment_count: true,
            caption: true,
          },
          post_action: 'instagram',
        },
        editing: 'source',
        styles: {
          theme: 'light',
          header_bg: '#ffffff',
          header_text: '#000000',
          follow_button_bg: '#0081ff',
          follow_button_text: '#ffffff',
          load_more_button_bg: '#0081ff',
          load_more_button_text: '#ffffff',
        },
      },
      datatable: {
        items: [],
        totalItems: 1,
        queries: {
          per_page: 10,
          page: 1,
          search: null,
          sort_by: 'id',
          sort_desc: true,
        },
      },
      default_editor: {
        source: {
          username: 'k.mbappe',
          type: 'username',
        },
        layout: {
          layoutStyle: 'grid',
          rows: 3,
          columns: 3,
          show_profile_details: true,
          profile_details: {
            profile_image: true,
            username: true,
            name: true,
            follow_button: true,
            post_count: true,
            followers: true,
            following: true,
          },
          padding: 5,
        },
        post_style: {
          post_style: 'image_card',
          post_details: {
            profile_image: true,
            username: true,
            likes_count: true,
            comment_count: true,
            caption: true,
          },
          post_action: 'instagram',
        },
        editing: 'layout',
        styles: {
          theme: 'light',
          header_bg: '#ffffff',
          header_text: '#000000',
          follow_button_bg: '#0081ff',
          follow_button_text: '#ffffff',
          load_more_button_bg: '#0081ff',
          load_more_button_text: '#ffffff',
        },
      },
    }
  },
  computed: {
    instaStreamGeneratorScriptSrc() {
      const isStaging = typeof window !== 'undefined' && window.location.origin.includes('staging.')
      return isStaging
        ? 'https://rengine.sfo3.cdn.digitaloceanspaces.com/staging/generator-scripts/v3/instastream-widget-generator.js'
        : 'https://rengine.sfo3.cdn.digitaloceanspaces.com/generator-scripts/v3/instastream-widget-generator.js'
    },
    embedHTML() {
      return `<!-- Start Instagram Widget -->
        &lt;script src="${this.instaStreamGeneratorScriptSrc}"&gt;&lt;/script&gt;
            &lt;instagram-widget widget-id='${this.selectedWidget}'&gt;&lt;/instagram-widget&gt;
        <!-- End Instagram Widget -->
        `
    },
    totalWidgets() {
      return this.widgets ? this.widgets.length : 0
    },
    visiblePostCount() {
      return (this.editor.layout?.columns || 3) * (this.editor.layout?.rows || 3) + this.additionalPostsToShow
    },
    visiblePosts() {
      const maxPosts = this.visiblePostCount
      const list = (this.posts || []).filter(post => post.media_url)
      return list.slice(0, maxPosts)
    },
    imageCardPosts() {
      return this.editor.post_style?.post_style === 'image_card' ? this.visiblePosts.filter(p => p.media_url) : []
    },
    expandedPosts() {
      return this.editor.post_style?.post_style === 'expanded' ? this.visiblePosts.filter(p => p.media_url) : []
    },
    showLoadMore() {
      return this.visiblePostCount < (this.posts || []).length
    },
  },
  watch: {
    'editor.styles.theme'(newTheme) {
      if (!this.editor.styles) return
      if (newTheme === 'dark') {
        this.editor.styles.header_bg = '#1b1b1b'
        this.editor.styles.header_text = '#ffffff'
        this.editor.styles.follow_button_bg = '#333333'
        this.editor.styles.follow_button_text = '#ffffff'
        this.editor.styles.load_more_button_bg = '#333333'
        this.editor.styles.load_more_button_text = '#ffffff'
      } else if (newTheme === 'light') {
        this.editor.styles.header_bg = '#ffffff'
        this.editor.styles.header_text = '#000000'
        this.editor.styles.follow_button_bg = '#0081ff'
        this.editor.styles.follow_button_text = '#ffffff'
        this.editor.styles.load_more_button_bg = '#0081ff'
        this.editor.styles.load_more_button_text = '#ffffff'
      }
    },
  },
  mounted() {
    this.getWidgets()
  },
  methods: {
    normalizeInstagramUsername(value) {
      const raw = ('' + (value || '')).trim().replace(/^@/, '')
      const cleaned = raw.replace(/[^a-zA-Z0-9._]/g, '').toLowerCase()
      return cleaned || ''
    },
    buildCreateEditor() {
      const editor = JSON.parse(JSON.stringify(this.default_editor))
      const seeded = this.normalizeInstagramUsername(this.newWidgetTitle)
      if (seeded) {
        editor.source.username = seeded
      }
      return editor
    },
    getWidgets() {
      this.loadingWidgets = true

      this.$store
        .dispatch('igwidget/getAllWidgets', {
          queries: this.datatable.queries,
          page: 'index',
        })
        .then((res) => {
          this.loadingWidgets = false
          this.widgets = res
        })
        .catch((error) => {
          this.loadingWidgets = false
          console.error('Error fetching widgets:', error)
        })
    },
    async createWidget() {
      if (!this.newWidgetTitle) {
        Vue.$toast.open({
          message: 'Please enter a widget title',
          type: 'error',
          duration: 5000,
        })
        return
      }
      this.gettingWidget = true
      try {
        const response = await this.$store.dispatch('igwidget/createWidget', {
          editor: this.buildCreateEditor(),
          widgetTitle: this.newWidgetTitle,
        })
        this.gettingWidget = false
        console.log('Widget created successfully:', response)
        this.showCreateWidgetModal = false
        this.newWidgetTitle = ''
        this.getWidgets()
        Vue.$toast.open({
          message: 'Widget created successfully',
          type: 'success',
          duration: 5000,
        })
        // Open edit modal after creation - you may want to navigate to edit page or open modal
        // For now, we'll just refresh the list
      } catch (error) {
        this.gettingWidget = false
        console.error('Error creating widget:', error)
        Vue.$toast.open({
          message: 'Error creating widget',
          type: 'error',
          duration: 5000,
        })
      }
    },
    editWidget(widget) {
      this.gettingWidget = true
      this.widgetId = widget.id
      const parsed = JSON.parse(widget.widget_settings || '{}')
      this.editor = this.normalizeEditorSettings(parsed)
      const username = (this.editor.source && this.editor.source.username) ? ('' + this.editor.source.username).trim().replace(/^@/, '') : ''
      if (username) {
        this.getWidgetForEdit()
      } else {
        this.profileFetchState = ''
        this.profile = null
        this.posts = []
        this.gettingWidget = false
        this.showEditWidgetModal = true
      }
    },
    normalizeEditorSettings(rawEditor = {}) {
      // Some older widgets persisted snake_case keys like layout.layout_style.
      // Normalize into the NewDesign camelCase structure so preview always renders.
      const parsed = rawEditor || {}
      const parsedLayout = parsed.layout || {}
      const parsedPostStyle = parsed.post_style || {}
      const parsedPostDetails = parsedPostStyle.post_details || {}

      const legacyLayoutStyle = parsedLayout.layout_style || parsed.layout_style
      const normalizedLayoutStyle = parsedLayout.layoutStyle || legacyLayoutStyle || this.default_editor.layout.layoutStyle

      const toPositiveInt = (value, fallback) => {
        const n = Number(value)
        return Number.isFinite(n) && n > 0 ? n : fallback
      }

      const normalizedLayout = {
        ...this.default_editor.layout,
        ...parsedLayout,
        layoutStyle: normalizedLayoutStyle,
        rows: toPositiveInt(parsedLayout.rows, this.default_editor.layout.rows),
        columns: toPositiveInt(parsedLayout.columns, this.default_editor.layout.columns),
        padding: toPositiveInt(parsedLayout.padding, this.default_editor.layout.padding),
        profile_details: {
          ...this.default_editor.layout.profile_details,
          ...(parsedLayout.profile_details || {}),
        },
      }

      return {
        ...this.default_editor,
        ...parsed,
        source: { ...this.default_editor.source, ...(parsed.source || {}) },
        layout: normalizedLayout,
        post_style: {
          ...this.default_editor.post_style,
          ...parsedPostStyle,
          post_details: {
            ...this.default_editor.post_style.post_details,
            ...parsedPostDetails,
          },
        },
        styles: { ...this.default_editor.styles, ...(parsed.styles || {}) },
      }
    },
    getWidgetForEdit(fetchProfile = false) {
      const raw = (this.editor.source.username || '').trim().replace(/^@/, '')
      if (!raw) {
        this.gettingWidget = false
        this.profileFetchState = ''
        this.profile = null
        this.posts = []
        if (!fetchProfile) {
          this.showEditWidgetModal = true
        }
        return
      }
      const username = raw
      this.profileFetchState = 'fetching'

      const handleError = (message) => {
        Vue.$toast.open({ message, type: 'error', duration: 5000 })
        this.gettingWidget = false
        this.profileFetchState = 'error'
        this.profileFetchMessage = message
        this.showEditWidgetModal = true
      }

      const handleSuccess = (res, postsType) => {
        if (res.businessDetails || res) {
          this.profile = res.businessDetails ?? null
          this.posts = res.businessDetails?.media?.data || res || []
          this.postsType = postsType
          this.gettingWidget = false
          this.profileFetchState = 'success'
          if (!fetchProfile) {
            this.showEditWidgetModal = true
          }
        } else {
          handleError('Invalid Username or You\'re trying to access a private account')
        }
      }

      const getApiErrorMessage = (err) => {
        const msg = err?.response?.data?.message || err?.response?.data?.error
        return (typeof msg === 'string' && msg) ? msg : null
      }

      if (this.editor.source.type === 'hashtag') {
        this.$store.dispatch('igwidget/searchKeyword', { username })
          .then((res) => handleSuccess(res, 'hashtag'))
          .catch((err) => handleError(getApiErrorMessage(err) || 'Invalid Hashtag or You\'re trying to access a private account'))
      } else {
        this.$store.dispatch('igwidget/getIgWidget', { username })
          .then((res) => handleSuccess(res, 'profile'))
          .catch((err) => handleError(getApiErrorMessage(err) || 'Invalid Username or You\'re trying to access a private account'))
      }
    },
    showWidgetModal() {
      this.showEditWidgetModal = true
    },
    formatNumber(value) {
      if (typeof value !== 'number' || isNaN(value)) return value
      const thresholds = [{ value: 1e9, suffix: 'B' }, { value: 1e6, suffix: 'M' }, { value: 1e3, suffix: 'K' }]
      for (const { value: threshold, suffix } of thresholds) {
        if (value >= threshold) return (value / threshold).toFixed(1) + suffix
      }
      return value.toString()
    },
    loadMore() {
      this.additionalPostsToShow += this.editor.layout.columns
    },
    post_action(post) {
      this.modalPost = post
      if (this.editor.post_style.post_action === 'instagram') {
        window.open(post.permalink, '_blank')
      } else {
        this.showPostModal = true
      }
    },
    redirectToProfile() {
      if (this.profile && this.profile.username) {
        window.open(`https://www.instagram.com/${this.profile.username}`, '_blank')
      }
    },
    async saveEditor(exit) {
      this.savingEditor = true
      try {
        await this.$store.dispatch('igwidget/saveEditor', {
          editor: this.editor,
          widgetId: this.widgetId,
        })
        this.savingEditor = false
        this.getWidgets()
        if (exit) {
          this.showEditWidgetModal = false
        }
        Vue.$toast.open({ message: 'Widget saved successfully.', type: 'success', duration: 5000 })
      } catch (error) {
        this.savingEditor = false
        console.error('Error saving widget:', error)
        Vue.$toast.open({ message: 'Error saving widget.', type: 'error', duration: 5000 })
      }
    },
    viewPage(widget) {
      this.gettingPage = true
      const settings = JSON.parse(widget.widget_settings)
      const username = settings.source.username
      window.open(`https://www.instagram.com/${username}`, '_blank')
      setTimeout(() => {
        this.gettingPage = false
      }, 500)
    },
    addWidget(widgetId) {
      this.selectedWidget = widgetId
      this.showAddWidgetModal = true
    },
    copyToClipboard() {
      const code = this.embedHTML.replaceAll('&lt;', '<').replaceAll('&gt;', '>')
      navigator.clipboard.writeText(code)
      Vue.$toast.open({
        message: 'Code Copied',
        type: 'success',
        duration: 5000,
      })
    },
    remove(widget) {
      this.showDeleteModal = true
      this.selectedBusiness = widget
      this.widgetId = widget.id
    },
    handleRemove() {
      this.showDeleteModal = false
      this.loadingRemove = this.widgetId

      this.$store
        .dispatch('igwidget/deleteWidget', this.widgetId)
        .then(() => {
          this.loadingRemove = null
          this.getWidgets()
        })
        .catch(() => {
          this.loadingRemove = null
          console.error('Error removing widget')
          Vue.$toast.open({
            message: 'Error removing widget',
            type: 'error',
            duration: 5000,
          })
        })
    },
    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getWidgets()
    },
  },
}
</script>

<style lang="scss" scoped>
// Create Customer Button
.create-customer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }
}

// Main Content

// Controls Bar
.controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.controls-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.show-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.show-select {
  height: 2.5rem;
  width: 5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: white;
  padding: 0 0.75rem;
  font-size: 0.875rem;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.search-input {
  height: 2.5rem;
  width: 16rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: white;
  padding: 0 0.75rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
}

// Table
.table-container {
  background: white;
  overflow: hidden;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table-header-row {
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table-header {
  height: 3rem;
  padding: 0 1rem;
  text-align: left;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.table-row {
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
}

.table-cell {
  padding: 1rem;
  color: #1f2937;
  font-size: 0.875rem;
}

// Buttons
.view-page-btn,
.add-widget-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.25rem;
  padding: 0 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: transparent;
  color: #000000;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.edit-widget-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
  background-color: #f9fafb;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  .edit-icon {
    width: 1rem;
    height: 1rem;
    color: #374151;
  }

  &:hover:not(:disabled) {
    background-color: #f3f4f6;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.375rem;
  border: none;
  background-color: #ef4444;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;

  .delete-icon {
    width: 1rem;
    height: 1rem;
    color: white;
  }

  &:hover:not(:disabled) {
    background-color: #dc2626;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.actions-group {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.loading-state {
  text-align: center;
  padding: 2rem;
}

// Pagination
.pagination-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #6b7280;
}

.pagination-total {
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

:deep(.page-item) {
  .page-link {
    border: 1px solid #d1d5db;
    color: #6b7280;
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    margin: 0 0.125rem;
    background: white;
    min-width: 2.25rem;
    height: 2.25rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(.disabled) {
      background-color: #f3f4f6;
      border-color: #9ca3af;
    }
  }

  &.active .page-link {
    background-color: #3b82f6;
    border-color: #3b82f6;
    color: white;
  }

  &.disabled .page-link {
    opacity: 0.5;
    cursor: not-allowed;
    color: #9ca3af;
    background: #f9fafb;
  }
}
</style>

<!-- Preview styles cannot be loaded via `style src` on another .vue — scoped CSS targets the wrong component. -->
<style lang="scss">
@import "../../../design/custom/components/insta-widget-editor-preview";

.instagram-stream-idle-message {
  padding: 2rem;
  text-align: center;
}

/* Business Instagram Widget Editor - same dark theme as Agency (high specificity) */
.edit_widget_modal.widget-editor.widget-modal {
  --insta-editor-sidebar: 222 47% 15%;
  --insta-editor-header: 222 47% 12%;
  --insta-editor-nav-active: 222 47% 22%;
  --insta-editor-card: 222 27% 22%;
  --insta-editor-text: 210 40% 98%;
  --insta-editor-muted: 215 20% 58%;
  --insta-editor-primary: 142 71% 45%;
}
.edit_widget_modal.widget-editor.widget-modal {
  .modal-dialog {
    margin: 0;
    .modal-content {
      width: 100vw;
      height: 100vh;
      border-radius: 0;
      display: flex !important;
      flex-direction: column !important;
    }
  }
  .modal-body,
  .modal-body.edit-widget-modal-body-wrapper {
    padding: 0 !important;
    flex: 1 !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }
  padding: 0 !important;

  .modal-content .modal-header {
    background: hsl(var(--insta-editor-header)) !important;
    color: hsl(var(--insta-editor-text)) !important;
    padding: 12px 20px !important;
    border-radius: 0 !important;
    border: 0 !important;
    flex-shrink: 0 !important;
  }

  .edit-widget-modal-body,
  .edit-widget-modal-body.insta-widget-editor-layout {
    display: flex !important;
    flex-direction: row !important;
    flex: 1 !important;
    min-height: 0 !important;
    width: 100% !important;
  }
  .edit-widget-modal-body .container-fluid {
    display: flex !important;
    flex: 1 !important;
    min-width: 0 !important;
    padding: 0 !important;
  }
  .edit-widget-modal-body .row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    flex: 1 !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .sidebar.insta-widget-editor-sidebar,
  #Navbar.sidebar {
    background: hsl(var(--insta-editor-sidebar)) !important;
    color: hsl(var(--insta-editor-text)) !important;
    padding: 20px 16px 20px 0 !important;
    min-height: calc(100vh - 52px) !important;
    flex: 0 0 360px !important;
    width: 360px !important;
    max-width: 360px !important;
    display: flex !important;
    flex-direction: row !important;
  }

  .left-nav-side {
    display: flex !important;
    flex-direction: column !important;
    width: 56px !important;
    min-width: 56px !important;
    flex-shrink: 0 !important;
    font-size: 11px !important;
    text-align: center !important;
    padding-top: 12px !important;

    button {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      padding: 12px 8px !important;
      margin-bottom: 4px !important;
      border: 0 !important;
      border-radius: 0 !important;
      appearance: none !important;
      background: transparent !important;
      color: hsl(var(--insta-editor-muted)) !important;
      cursor: pointer !important;
      transition: color 0.15s, background 0.15s !important;

      i {
        font-size: 22px !important;
        line-height: 1.2 !important;
        margin-bottom: 4px !important;
      }

      &:hover {
        color: hsl(var(--insta-editor-text)) !important;
      }

      &.active {
        background: hsl(var(--insta-editor-nav-active)) !important;
        color: hsl(var(--insta-editor-text)) !important;
      }
    }
  }

  .editor-content,
  .editor-content.instagram-widget {
    flex: 1 !important;
    min-width: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  .instagram-widget {
    overflow-y: auto !important;
    overflow-x: hidden !important;
  }

  --track-width: 45px !important;
  --track-height: 25px !important;
  --track-active-color: hsl(var(--insta-editor-primary)) !important;

  .insta-widget-editor-sidebar .nav-sidebar,
  #Navbar.sidebar .nav-sidebar {
    width: calc(100% - 56px) !important;
    padding-left: 16px !important;
    overflow-x: hidden !important;

    > li {
      list-style: none !important;
      width: 100% !important;

      > h4 {
        margin-top: 0 !important;
        margin-bottom: 16px !important;
        font-size: 1.25rem !important;
        font-weight: 600 !important;
        color: hsl(var(--insta-editor-text)) !important;
      }

      > ul {
        > li {
          background: hsl(var(--insta-editor-card)) !important;
          border-radius: 8px !important;
          padding: 10px 12px !important;
          margin-bottom: 10px !important;
          width: 100% !important;
          clear: both !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          color: hsl(var(--insta-editor-text)) !important;

          > span {
            flex: 1 !important;
            flex-wrap: nowrap !important;
            text-align: left !important;
          }

          .vue-js-switch {
            margin-bottom: 0 !important;
            flex-shrink: 0 !important;
            margin-left: auto !important;
          }
        }
      }
    }

    .layouts_wrapper {
      display: flex !important;
      gap: 10px !important;
      flex-wrap: wrap !important;

      .layout-item {
        flex: 1 !important;
        min-width: 0 !important;
        max-width: 50% !important;
      }

      .layout-item .btn,
      .layout-item label.btn,
      .layout-option-btn {
        background: hsl(var(--insta-editor-card)) !important;
        border: 2px solid hsl(var(--insta-editor-card)) !important;
        color: hsl(var(--insta-editor-text)) !important;
        border-radius: 8px !important;
        padding: 8px !important;
        width: 100% !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        min-height: 70px !important;

        img {
          max-width: 48px !important;
          max-height: 48px !important;
          margin-bottom: 4px !important;
        }

        span {
          font-size: 12px !important;
        }

        &:hover {
          border-color: hsl(var(--insta-editor-muted)) !important;
        }
      }

      .btn-check:checked + .btn,
      .btn-check:checked + label.btn,
      .btn-check:checked + .layout-option-btn {
        border-color: hsl(var(--insta-editor-primary)) !important;
        background: hsl(var(--insta-editor-nav-active)) !important;
      }
    }

    .source > ul > li {
      &.d-flex {
        display: flex !important;
        align-items: center !important;
        gap: 0 !important;

        .source_input {
          flex: 1 !important;
          min-width: 0 !important;
          margin-right: 0 !important;
        }

        .source_button {
          flex-shrink: 0 !important;
          margin-left: 4px !important;
        }
      }

      &.widget_theme .layout-item {
        width: 100% !important;
        align-items: center !important;
      }
    }

    .post_style_wrapper .layout-item,
    .post_action_wrapper .layout-item,
    .widget_theme .layout-item {
      width: 100% !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      flex-wrap: nowrap !important;

      > span:first-child {
        flex: 1 !important;
        min-width: 0 !important;
      }
    }

    .post_action_wrapper .v-select .vs__selected-options,
    .post_style_wrapper .v-select .vs__selected-options {
      flex-wrap: nowrap !important;
    }

    .header_styles {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;

      > span {
        flex: 1 !important;
      }
    }

    .profile_details {
      list-style: none !important;
      padding: 0 !important;
      margin: 0 0 10px 0 !important;
      width: 100% !important;
    }

    .profile_details li {
      background: hsl(var(--insta-editor-card)) !important;
      border-radius: 8px !important;
      padding: 6px 12px !important;
      margin-bottom: 4px !important;
      color: hsl(var(--insta-editor-text)) !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;

      > span {
        flex: 1 !important;
        text-align: left !important;
      }

      .vue-js-switch {
        flex-shrink: 0 !important;
        margin-left: auto !important;
      }
    }

    .v-select {
      min-width: 120px !important;
      width: 120px !important;
      flex-shrink: 0 !important;

      .vs__open-indicator {
        display: none !important;
      }

      .vs__selected {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
      }
    }

    .post_style_wrapper .v-select,
    .post_action_wrapper .v-select {
      min-width: 150px !important;
      width: 150px !important;
    }
    .v-select ul[role="listbox"] {
      z-index: 10000 !important;
      color: #000 !important;
      text-align: left !important;

      li {
        text-align: left !important;
        white-space: nowrap !important;
      }
    }
  }
}
</style>

<!-- Unscoped: vue-select dropdown is appended to body, needs global z-index to appear above modal -->
<style lang="scss">
.vs__dropdown-menu {
  z-index: 10000 !important;
}
</style>

